import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import { Loader2 } from "lucide-react";

/**
 * Bettet das Xentra Direktbuchungs-Widget ein — IMMER in einem sandboxed <iframe>.
 *
 * Warum ein Sandbox-iFrame: Das Skript, das Xentra liefert, navigiert bei der Suche selbst
 * per JavaScript auf eine andere Domain (z.B. book.myxentra.com) - das ist Verhalten des
 * Xentra-Codes selbst, nicht etwas, das sich per CSS/Konfiguration abschalten lässt.
 * Ein <iframe sandbox="..."> OHNE "allow-top-navigation" verhindert auf Browser-Ebene,
 * dass ein Skript innerhalb des Frames die komplette Seite (den Tab) wegnavigiert.
 * Das Widget bleibt dadurch technisch erzwungen auf atlas-apartment.de eingebettet.
 *
 * Hinweis: Sollte der letzte Bezahlschritt bei Xentra einen echten Redirect ausserhalb des
 * Frames benötigen (z.B. 3-D-Secure/PayPal), kann das diesen einen Schritt evtl. verhindern.
 * Dann in ALLOWED_ON_USER_CLICK unten "allow-top-navigation-by-user-activation" aktivieren -
 * das blockt nur automatische Redirects, erlaubt aber echte Klicks des Gastes.
 */
const SANDBOX_STRICT = "allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-top-navigation-by-user-activation";

function buildSrcDoc(embedCode) {
  // WICHTIG: <base href> muss auf die Domain zeigen, von der das Xentra-Skript kommt.
  // Ohne das versucht der Browser, relative Pfade (z.B. das Widget-CSS) von unserer
  // eigenen Domain zu laden statt von Xentra - das schlägt fehl (CORB-Block, leeres/
  // ungestyltes Widget), weil unser Server dort kein gültiges CSS zurückgibt.
  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base href="https://api.myxentra.com/" target="_self" />
    <style>
      html,body{margin:0;padding:0;background:transparent;font-family:Inter,sans-serif;}
    </style>
  </head><body>${embedCode}</body></html>`;
}

export default function XentraWidget({ className = "" }) {
  const { lang } = useLang();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(900);

  useEffect(() => {
    api.get("/config/xentra-widget")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ embed_url: "", embed_code: "" }))
      .finally(() => setLoading(false));
  }, []);

  // Falls das Widget selbst per postMessage seine Höhe meldet (üblich bei Booking-Widgets),
  // die iframe-Höhe automatisch anpassen statt einer festen Scroll-Box.
  useEffect(() => {
    const onMessage = (e) => {
      if (typeof e.data === "object" && e.data?.xentraHeight) {
        setFrameHeight(Math.max(300, Number(e.data.xentraHeight)));
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Bei srcDoc-Einbettung (mit allow-same-origin) können wir direkt auf das innere Dokument
  // zugreifen und die echte Inhaltshöhe messen — statt raten zu müssen. Das behebt sowohl
  // "zu klein mit Scrollbalken" als auch "zu viel weißer Leerraum darunter".
  const measureAndObserve = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc?.body) return;
      const measure = () => {
        const h = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
        if (h > 0) setFrameHeight(Math.max(300, h));
      };
      measure();
      resizeObserverRef.current?.disconnect();
      const ro = new ResizeObserver(measure);
      ro.observe(doc.body);
      resizeObserverRef.current = ro;
    } catch {
      // Cross-Origin-Zugriff nicht möglich (z.B. bei externer embed_url) — dann bleibt die
      // aktuelle/Standardhöhe bzw. die per postMessage gemeldete Höhe bestehen.
    }
  };

  useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-24 ${className}`}>
        <Loader2 size={28} className="text-[var(--terra)] animate-spin" />
      </div>
    );
  }

  if (config?.embed_code) {
    return (
      <div className={`w-full ${className}`} style={{ height: frameHeight, transition: "height 0.2s ease" }}>
        <iframe
          ref={iframeRef}
          title="Xentra Buchungswidget"
          sandbox={SANDBOX_STRICT}
          srcDoc={buildSrcDoc(config.embed_code)}
          onLoad={measureAndObserve}
          className="w-full h-full border-0"
        />
      </div>
    );
  }

  if (config?.embed_url) {
    return (
      <div className={`w-full ${className}`} style={{ minHeight: "70vh" }}>
        <iframe
          src={config.embed_url}
          title="Xentra Buchungswidget"
          sandbox={SANDBOX_STRICT}
          className="w-full border-0"
          style={{ minHeight: "70vh", height: "100%" }}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className={`bg-[var(--warm)] border border-dashed border-[var(--muted)]/40 rounded-[24px] p-12 text-center ${className}`}>
      <p className="text-[var(--muted)] text-sm max-w-md mx-auto">
        {lang === "de"
          ? "Das Buchungswidget ist noch nicht eingerichtet. Bitte im Admin-Bereich unter Integrations → Xentra die Widget-URL oder den Embed-Code hinterlegen."
          : "The booking widget hasn't been set up yet. Please add the widget URL or embed code under Admin → Integrations → Xentra."}
      </p>
    </div>
  );
}
